import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  Alert,
  Paper,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';

const UPGRADE_HREF = '/manage-account?tab=subscription';

type GroupedPlanLimit = {
  collectionNames: string[];
  requestNames: string[];
  dataSetRequestNames: string[];
  environmentDetails: string[];
  collectionReason?: string;
  requestReason?: string;
  dataSetReason?: string;
  collectionReasonsList?: string[];
  requestReasonsList?: string[];
  dataSetReasonsList?: string[];
};

function groupPlanLimitErrors(errors: string[]): { grouped: GroupedPlanLimit; remainder: string[] } {
  const grouped: GroupedPlanLimit = {
    collectionNames: [],
    requestNames: [],
    dataSetRequestNames: [],
    environmentDetails: [],
  };
  const collectionReasons = new Set<string>();
  const requestReasons = new Set<string>();
  const dataSetReasons = new Set<string>();
  const remainder: string[] = [];

  for (const raw of errors || []) {
    const err = typeof raw === 'string' ? raw.trim() : String(raw);
    if (!err) continue;

    if (!err.includes('Plan limit exceeded')) {
      remainder.push(err);
      continue;
    }

    let m = err.match(/^Plan limit exceeded: environment not created\.\s*(.+)$/i);
    if (m) {
      grouped.environmentDetails.push(m[1]);
      continue;
    }

    m = err.match(/^Plan limit exceeded: skipped collection '(.+?)'\s*(?:\([^)]*\))?\.\s*(.+)$/i);
    if (m) {
      grouped.collectionNames.push(m[1]);
      collectionReasons.add(m[2]);
      continue;
    }

    m = err.match(/^Plan limit exceeded: skipped request '(.+?)'\.\s*(.+)$/i);
    if (m) {
      grouped.requestNames.push(m[1]);
      requestReasons.add(m[2]);
      continue;
    }

    m = err.match(/^Plan limit exceeded: skipped data set for request '(.+?)'\.\s*(.+)$/i);
    if (m) {
      grouped.dataSetRequestNames.push(m[1]);
      dataSetReasons.add(m[2]);
      continue;
    }

    remainder.push(err);
  }

  if (collectionReasons.size === 1) {
    grouped.collectionReason = collectionReasons.values().next().value;
  } else if (collectionReasons.size > 1) {
    grouped.collectionReasonsList = Array.from(collectionReasons);
  }

  if (requestReasons.size === 1) {
    grouped.requestReason = requestReasons.values().next().value;
  } else if (requestReasons.size > 1) {
    grouped.requestReasonsList = Array.from(requestReasons);
  }

  if (dataSetReasons.size === 1) {
    grouped.dataSetReason = dataSetReasons.values().next().value;
  } else if (dataSetReasons.size > 1) {
    grouped.dataSetReasonsList = Array.from(dataSetReasons);
  }

  return { grouped, remainder };
}

function dedupeEnvDetails(details: string[]): string[] {
  return Array.from(new Set(details));
}

function ReasonLines(props: {
  single?: string;
  multiple?: string[];
}) {
  const { single, multiple } = props;
  if (multiple && multiple.length > 0) {
    return (
      <List dense disablePadding sx={{ mt: 0.5 }}>
        {multiple.map((line, i) => (
          <ListItem key={`${i}-${line.slice(0, 40)}`} disablePadding sx={{ py: 0.25 }}>
            <ListItemText primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }} primary={line} />
          </ListItem>
        ))}
      </List>
    );
  }
  if (single) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {single}
      </Typography>
    );
  }
  return null;
}

const ImportSwaggerResult: React.FC<{ result: any; onClose: () => void; onReset: () => void }> = ({
  result,
  onClose,
  onReset,
}) => {
  const parseSkipped = (message: string) => {
    let collectionsSkipped = 0;
    let requestsSkipped = 0;
    if (message) {
      const colMatch = message.match(/Collections Skipped: (\d+)/);
      const reqMatch = message.match(/Requests Skipped: (\d+)/);
      if (colMatch) collectionsSkipped = parseInt(colMatch[1], 10);
      if (reqMatch) requestsSkipped = parseInt(reqMatch[1], 10);
    }
    return { collectionsSkipped, requestsSkipped };
  };

  const collectionsCreated = result?.collectionsCreated ?? 0;
  const requestsCreated = result?.requestsCreated ?? 0;
  const dataSetsCreated = result?.dataSetsCreated ?? 0;
  const dataSetsError = result?.dataSetsError;
  const assertionsCreated = result?.assertionsCreated ?? 0;
  const assertionsError = result?.assertionsError;
  const { collectionsSkipped, requestsSkipped } = parseSkipped(result?.message);

  const errorsRaw = result?.errors;
  const hasImportErrors = Array.isArray(errorsRaw) && errorsRaw.length > 0;
  const { grouped: planGrouped, remainder: otherErrors } = useMemo(
    () => groupPlanLimitErrors(Array.isArray(errorsRaw) ? errorsRaw : []),
    [errorsRaw]
  );

  const importedAny =
    collectionsCreated > 0 || requestsCreated > 0 || dataSetsCreated > 0 || assertionsCreated > 0;

  const duplicateOnlyNothingImported =
    result?.success !== false &&
    collectionsCreated === 0 &&
    requestsCreated === 0 &&
    (collectionsSkipped > 0 ||
      requestsSkipped > 0 ||
      (typeof result?.message === 'string' &&
        result.message.includes('All selected items already exist in this project')));

  const planBlockedEverything =
    result?.success &&
    hasImportErrors &&
    !importedAny &&
    !duplicateOnlyNothingImported;

  const partialSuccess =
    result?.success && hasImportErrors && importedAny;

  const parseSummary = (message: string) => {
    const lines = message.split('\n');
    const summary: any = {
      success: {},
      skipped: {},
      failed: {},
    };

    let currentSection = '';
    lines.forEach((line) => {
      if (line.includes('Success:')) {
        currentSection = 'success';
      } else if (line.includes('Skipped (already exist):')) {
        currentSection = 'skipped';
      } else if (line.includes('Failed:')) {
        currentSection = 'failed';
      } else if (line.includes('Collections Created:') || line.includes('Requests Created:')) {
        const parts = line.split(':').map((s) => s.trim());
        const key = parts[0];
        const value = parts.slice(1).join(':').trim();
        if (currentSection === 'success' && key && value !== undefined) {
          summary.success[key] = parseInt(value, 10);
        }
      } else if (line.includes('Collections Skipped:') || line.includes('Requests Skipped:')) {
        const parts = line.split(':').map((s) => s.trim());
        const key = parts[0];
        const value = parts.slice(1).join(':').trim();
        if (currentSection === 'skipped' && key && value !== undefined) {
          summary.skipped[key] = parseInt(value, 10);
        }
      } else if (line.includes('Collections Failed:') || line.includes('Requests Failed:')) {
        const parts = line.split(':').map((s) => s.trim());
        const key = parts[0];
        const value = parts.slice(1).join(':').trim();
        if (currentSection === 'failed' && key && value !== undefined) {
          summary.failed[key] = parseInt(value, 10);
        }
      }
    });

    return summary;
  };

  const summary = result?.message ? parseSummary(result.message) : {};

  const planLimitIssueCount =
    planGrouped.collectionNames.length +
    planGrouped.requestNames.length +
    planGrouped.dataSetRequestNames.length +
    planGrouped.environmentDetails.length;

  const renderNameList = (names: string[], label: string) => {
    if (names.length === 0) return null;
    const maxShow = 25;
    const shown = names.slice(0, maxShow);
    const rest = names.length - shown.length;
    return (
      <Box sx={{ mt: 1, pl: 1, borderLeft: 2, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label} ({names.length})
        </Typography>
        <List dense disablePadding sx={{ maxHeight: 200, overflow: 'auto' }}>
          {shown.map((name, i) => (
            <ListItem key={`${name}-${i}`} disablePadding sx={{ py: 0 }}>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }} primary={name} />
            </ListItem>
          ))}
          {rest > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 2 }}>
              …and {rest} more
            </Typography>
          )}
        </List>
      </Box>
    );
  };

  return (
    <Box sx={{ py: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Import Report
      </Typography>

      {duplicateOnlyNothingImported ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No new collections or requests were imported. All items already exist in the database.
        </Alert>
      ) : planBlockedEverything ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Import could not add new resources
          </Typography>
          <Typography variant="body2">
            Nothing new was imported. Remaining items were blocked by your plan limits. Review the sections below, then
            upgrade your plan if you need higher limits.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            component={RouterLink}
            to={UPGRADE_HREF}
            sx={{ mt: 1.5 }}
          >
            Review plan &amp; upgrade
          </Button>
        </Alert>
      ) : partialSuccess ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Import completed with limitations
          </Typography>
          <Typography variant="body2">
            Some resources were imported successfully, but other items could not be created because of your plan limits.
            Details are grouped below.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            size="small"
            component={RouterLink}
            to={UPGRADE_HREF}
            sx={{ mt: 1.5 }}
          >
            Review plan &amp; upgrade
          </Button>
        </Alert>
      ) : result?.success ? (
        <Alert severity="success" sx={{ mb: 2 }}>
          Import completed successfully.
        </Alert>
      ) : (
        <Alert severity="error" sx={{ mb: 2 }}>
          {result.message || 'Import failed!'}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Summary
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {importedAny ? 'Imported in this run' : 'Nothing new was imported in this run'}
          </Typography>
          <List dense>
            <ListItem>
              <ListItemText primary={`Collections created: ${collectionsCreated}`} />
            </ListItem>
            <ListItem>
              <ListItemText primary={`Requests created: ${requestsCreated}`} />
            </ListItem>
            {dataSetsCreated > 0 && (
              <ListItem>
                <ListItemText primary={`Data sets created: ${dataSetsCreated}`} />
              </ListItem>
            )}
            {assertionsCreated > 0 && (
              <ListItem>
                <ListItemText primary={`Assertions created (follow-up step): ${assertionsCreated}`} />
              </ListItem>
            )}
          </List>
        </Box>

        {hasImportErrors && planLimitIssueCount > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="warning.main" sx={{ fontWeight: 600, mb: 1 }}>
              Blocked by plan limits ({planLimitIssueCount} item{planLimitIssueCount === 1 ? '' : 's'})
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The messages below are grouped by type. Repeated plan text is shown once per group.
            </Typography>

            {planGrouped.environmentDetails.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Environment
                </Typography>
                {dedupeEnvDetails(planGrouped.environmentDetails).map((line) => (
                  <Typography key={line} variant="body2" color="error.main" sx={{ mt: 0.5 }}>
                    {line}
                  </Typography>
                ))}
              </Box>
            )}

            {planGrouped.collectionNames.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Collections not created
                </Typography>
                <ReasonLines
                  single={planGrouped.collectionReason}
                  multiple={planGrouped.collectionReasonsList}
                />
                {renderNameList(planGrouped.collectionNames, 'Skipped collections')}
              </Box>
            )}

            {planGrouped.requestNames.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Requests not created
                </Typography>
                <ReasonLines single={planGrouped.requestReason} multiple={planGrouped.requestReasonsList} />
                {renderNameList(planGrouped.requestNames, 'Skipped requests')}
              </Box>
            )}

            {planGrouped.dataSetRequestNames.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Data sets not created
                </Typography>
                <ReasonLines single={planGrouped.dataSetReason} multiple={planGrouped.dataSetReasonsList} />
                {renderNameList(planGrouped.dataSetRequestNames, 'Requests missing default data set')}
              </Box>
            )}

            <Button variant="outlined" size="small" component={RouterLink} to={UPGRADE_HREF}>
              Upgrade plan for higher limits
            </Button>
          </Box>
        )}

        {summary.success && Object.keys(summary.success).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 'bold' }}>
                  From import summary
                </Typography>
              </Box>
              <List dense>
                {summary.success['Collections Created'] > 0 && (
                  <ListItem>
                    <ListItemText primary={`Collections created: ${summary.success['Collections Created']}`} />
                  </ListItem>
                )}
                {summary.success['Requests Created'] > 0 && (
                  <ListItem>
                    <ListItemText primary={`Requests created: ${summary.success['Requests Created']}`} />
                  </ListItem>
                )}
              </List>
            </Box>
          </>
        )}

        {dataSetsError && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <WarningIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="warning.main" sx={{ fontWeight: 'bold' }}>
                Data sets (follow-up step)
              </Typography>
            </Box>
            <Alert severity="warning" sx={{ mt: 1 }}>
              {dataSetsError}
            </Alert>
          </Box>
        )}

        {assertionsError && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <WarningIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="warning.main" sx={{ fontWeight: 'bold' }}>
                Assertions (follow-up step)
              </Typography>
            </Box>
            <Alert severity="warning" sx={{ mt: 1 }}>
              {assertionsError}
            </Alert>
          </Box>
        )}

        {summary.skipped && Object.keys(summary.skipped).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <WarningIcon color="warning" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="warning.main" sx={{ fontWeight: 'bold' }}>
                Skipped (already exist)
              </Typography>
            </Box>
            <List dense>
              {summary.skipped['Collections Skipped'] > 0 && (
                <ListItem>
                  <ListItemText primary={`Collections skipped: ${summary.skipped['Collections Skipped']}`} />
                </ListItem>
              )}
              {summary.skipped['Requests Skipped'] > 0 && (
                <ListItem>
                  <ListItemText primary={`Requests skipped: ${summary.skipped['Requests Skipped']}`} />
                </ListItem>
              )}
            </List>
          </Box>
        )}

        {summary.failed && Object.keys(summary.failed).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <ErrorIcon color="error" sx={{ mr: 1 }} />
              <Typography variant="subtitle2" color="error.main" sx={{ fontWeight: 'bold' }}>
                Failed (summary counts)
              </Typography>
            </Box>
            <List dense>
              {summary.failed['Collections Failed'] > 0 && (
                <ListItem>
                  <ListItemText primary={`Collections failed: ${summary.failed['Collections Failed']}`} />
                </ListItem>
              )}
              {summary.failed['Requests Failed'] > 0 && (
                <ListItem>
                  <ListItemText primary={`Requests failed: ${summary.failed['Requests Failed']}`} />
                </ListItem>
              )}
            </List>
          </Box>
        )}
      </Paper>

      {otherErrors.length > 0 && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
            Other issues
          </Typography>
          <List dense sx={{ maxHeight: 220, overflow: 'auto' }}>
            {otherErrors.slice(0, 8).map((error: string, idx: number) => (
              <ListItem key={idx}>
                <ListItemText primary={error} sx={{ color: 'error.main' }} />
              </ListItem>
            ))}
            {otherErrors.length > 8 && (
              <ListItem>
                <ListItemText
                  primary={`…and ${otherErrors.length - 8} more`}
                  primaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      )}

      <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            height: 36,
            minHeight: 36,
            fontSize: '0.875rem',
            bgcolor: '#1d4ed8',
            boxShadow: 'none',
            '&:hover': { bgcolor: '#1e40af' },
          }}
        >
          Close
        </Button>
        <Button
          variant="outlined"
          onClick={onReset}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            height: 36,
            minHeight: 36,
            fontSize: '0.875rem',
          }}
        >
          Import Another
        </Button>
      </Box>
    </Box>
  );
};

export default ImportSwaggerResult;
